"use client";

import { Meter as MeterPrimitive } from "@base-ui/react/meter";
import type React from "react";
import { cn } from "../../lib/utils";
import type { Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";
import {
  GAUGE_INDICATOR_CLASS,
  GAUGE_TRACK_CLASS,
  GAUGE_TRACK_THICKNESS,
  type GaugeThickness,
} from "./gauge-recipe";

export function Meter({
  className,
  children,
  ...props
}: MeterPrimitive.Root.Props): React.ReactElement {
  return (
    <MeterPrimitive.Root
      className={cn("flex w-full flex-col gap-2", className)}
      data-slot="meter"
      {...props}
    >
      {children ? (
        children
      ) : (
        <MeterTrack>
          <MeterIndicator />
        </MeterTrack>
      )}
    </MeterPrimitive.Root>
  );
}

export function MeterLabel({
  className,
  ...props
}: MeterPrimitive.Label.Props): React.ReactElement {
  return (
    <MeterPrimitive.Label
      className={cn("font-medium text-foreground text-sm", className)}
      data-slot="meter-label"
      {...props}
    />
  );
}

/**
 * Track thickness on the gauge scale (`gauge-recipe.ts`). A meter is content
 * — a stat cell's gauge — so it rests on the 8px `lg` rung; `sm` is the
 * chrome rung, a 4px pill for a pane footer or a table cell.
 */
export type MeterTrackProps = MeterPrimitive.Track.Props &
  Omittable<{ size?: GaugeThickness }>;

export function MeterTrack({
  size = "lg",
  className,
  ...props
}: MeterTrackProps): React.ReactElement {
  return (
    <MeterPrimitive.Track
      className={cn(GAUGE_TRACK_CLASS, GAUGE_TRACK_THICKNESS[size], className)}
      data-slot="meter-track"
      {...props}
    />
  );
}

export type MeterIndicatorProps = MeterPrimitive.Indicator.Props &
  Omittable<{ color?: Color; pattern?: "stripes" }>;

/**
 * The fill. `color` travels as a NAME through `data-color` (`bg-c`), the same
 * mechanism {@link MeterRing} uses, so a meter whose hue carries meaning — a
 * headroom gauge crossing into warning, a categorical share — names the color
 * instead of reaching for a class. Omit it for the `primary` fill: magnitude
 * alone, one hue across a ranked set.
 *
 * `pattern="stripes"` lays a slowly drifting 45deg hatch over the fill (the
 * `[data-pattern="stripes"]` rule in `index.css`), for a budget that is being
 * consumed while the reader watches it. The hatch takes its ink from the fill
 * color's own `-foreground` token, so it stays legible on green, amber and
 * red alike, and it holds still under `prefers-reduced-motion`. A ranked set
 * takes no pattern: the hatch says "live", and on a static comparison it says
 * nothing while costing legibility at these sizes.
 */
export function MeterIndicator({
  color,
  pattern,
  className,
  ...props
}: MeterIndicatorProps): React.ReactElement {
  return (
    <MeterPrimitive.Indicator
      className={cn(GAUGE_INDICATOR_CLASS, color == null ? "bg-primary" : "bg-c", className)}
      data-color={color}
      data-pattern={pattern}
      data-slot="meter-indicator"
      {...props}
    />
  );
}

export function MeterValue({
  className,
  ...props
}: MeterPrimitive.Value.Props): React.ReactElement {
  return (
    <MeterPrimitive.Value
      className={cn("text-foreground text-sm tabular-nums", className)}
      data-slot="meter-value"
      {...props}
    />
  );
}

/**
 * A compact circular gauge — the meter at glyph scale, for stat cells and
 * footers where a full-width track has no room. Purely presentational
 * (`aria-hidden`): it renders inside a `Meter` root, which carries the value
 * for the accessibility tree; the visible number is the sibling text.
 *
 * The arc starts at 12 o'clock and fills clockwise. `color` travels as a
 * NAME through `data-color` (`stroke-c`), falling back to the same neutral
 * every colorless `c` consumer gets; the track is the `input` hairline tone,
 * quiet in both modes.
 */
export function MeterRing({
  value,
  max = 100,
  size = 16,
  thickness = 2,
  color,
  className,
}: Omittable<{
  value: number;
  max?: number;
  /** Outer diameter in px. */
  size?: number;
  /** Stroke width in px. */
  thickness?: number;
  color?: Color;
  className?: string;
}>): React.ReactElement {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  return (
    <svg
      aria-hidden
      className={cn("-rotate-90 shrink-0", className)}
      data-color={color}
      data-slot="meter-ring"
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
    >
      <circle
        className="stroke-input"
        cx={size / 2}
        cy={size / 2}
        fill="none"
        r={radius}
        strokeWidth={thickness}
      />
      <circle
        className="stroke-c"
        cx={size / 2}
        cy={size / 2}
        fill="none"
        r={radius}
        strokeDasharray={`${fraction * circumference} ${circumference}`}
        strokeLinecap="round"
        strokeWidth={thickness}
      />
    </svg>
  );
}
